import React, { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { SITE_ASSETS } from "../mock";
import { useSettings } from "../context/SettingsContext";

/**
 * Petralithe-style sticky parallax hero.
 * Five layers stacked, scroll-driven transforms:
 *   1. Sky (slow drift up)
 *   2. City Skyline (medium drift)
 *   3. Palm trees (foreground)
 *   4. Building (zoom-in)
 *   5. Tamra Logo + headline (fade-in)
 */
export default function HeroParallax() {
  const ref = useRef(null);
  const [progress, setProgress] = useState(0); // 0 → 1 across stage
  const { settings } = useSettings();

  useEffect(() => {
    const onScroll = () => {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const total = rect.height - window.innerHeight;
      const scrolled = Math.min(Math.max(-rect.top, 0), total);
      setProgress(total > 0 ? scrolled / total : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Easing helpers
  const p = progress;
  const skyY = -p * 120;                       // slow up
  const skylineY = -p * 260;                   // faster up
  const skylineOpacity = 1 - Math.max(0, (p - 0.5) * 2.0);
  const palmsY = -p * 380;                     // fastest foreground
  const palmsOpacity = 1 - Math.max(0, (p - 0.45) * 2.3);
  const buildingScale = 1 + p * 0.35;
  const buildingOpacity = Math.min(1, Math.max(0, (p - 0.15) * 1.6));
  const logoOpacity = Math.min(1, Math.max(0, (p - 0.55) * 2.6));
  const logoScale = 0.7 + Math.min(0.3, p * 0.45);
  const headlineOpacity = 1 - Math.min(1, p * 1.6);

  // Letter-by-letter render
  const headlineWord = (settings.heroHeadline || "LUXURY HIGH-RISE LIVING").toUpperCase();

  return (
    <section ref={ref} className="hero-stage">
      <div className="hero-sticky">
        {/* Layer 1 — Sky */}
        <div className="hero-layer" style={{ transform: `translateY(${skyY}px) scale(1.05)` }}>
          <img src={SITE_ASSETS.hero.sky} alt="sky" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#F5EFE6]/0 via-[#F5EFE6]/0 to-[#F5EFE6]"></div>
        </div>

        {/* Layer 2 — City skyline silhouette */}
        <div
          className="hero-layer"
          style={{ transform: `translateY(${skylineY}px)`, opacity: skylineOpacity }}
        >
          <img src={SITE_ASSETS.hero.skyline} alt="skyline" className="object-cover object-bottom" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1F1B17]/10 via-transparent to-[#F5EFE6]"></div>
        </div>

        {/* Layer 3 — Palms */}
        <div
          className="hero-layer"
          style={{ transform: `translateY(${palmsY}px)`, opacity: palmsOpacity }}
        >
          <img src={SITE_ASSETS.hero.palms} alt="palms" className="object-cover object-bottom" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#1F1B17]/30 to-[#1F1B17]/70"></div>
        </div>

        {/* Layer 4 — Tamra building (signature elevation) */}
        <div
          className="hero-layer"
          style={{ transform: `scale(${buildingScale})`, opacity: buildingOpacity }}
        >
          <img src={SITE_ASSETS.hero.building} alt="Tamra building" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1F1B17]/80 via-[#1F1B17]/20 to-transparent"></div>
        </div>

        {/* Headline (top, fades out as we scroll) */}
        <div
          className="absolute inset-x-0 top-0 z-20 pointer-events-none"
          style={{ opacity: headlineOpacity }}
        >
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-32 lg:pt-36 text-center">
            {/* Soft cream backdrop blur behind text for legibility */}
            <div className="relative inline-block">
              <div className="absolute -inset-x-12 -inset-y-6 bg-[#F5EFE6]/35 backdrop-blur-md rounded-3xl"></div>
              <div className="relative">
                <div className="text-[11px] tracking-[0.5em] text-[#5C2018] mb-5 uppercase font-semibold">Tamra · Reimagined</div>
                <h1 className="font-display headline-tight text-[#1F1B17] text-[clamp(2.2rem,6vw,5.5rem)] leading-[1.05] spaced-letters">
                  {headlineWord.split("").map((ch, i) => (
                    <span key={i} style={{ animationDelay: `${i * 30}ms` }}>{ch === " " ? "\u00A0\u00A0" : ch}</span>
                  ))}
                </h1>
                <p className="mt-6 text-[#3a322a] text-base md:text-lg max-w-2xl mx-auto italic font-display font-medium">{settings.heroSubline}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Logo reveal at end */}
        <div className="absolute inset-0 z-30 flex items-center justify-center pointer-events-none" style={{ opacity: logoOpacity }}>
          <div className="text-center" style={{ transform: `scale(${logoScale})` }}>
            <div className="w-40 h-40 md:w-56 md:h-56 mx-auto rounded-full bg-[#F5EFE6]/95 flex items-center justify-center shadow-2xl ring-1 ring-[#B8893C]/40">
              <img src={SITE_ASSETS.logo} alt="Tamra Developers" className="w-32 md:w-44 object-contain" />
            </div>
            <div className="mt-8 text-[#F5EFE6]">
              <div className="text-[11px] tracking-[0.5em] uppercase text-[#B8893C]">Welcome to</div>
              <div className="font-display text-4xl md:text-6xl tracking-[0.2em] mt-3">{settings.brandName}</div>
              <div className="text-[11px] tracking-[0.5em] uppercase text-[#B8893C] mt-3">{settings.brandSubtitle}</div>
            </div>
          </div>
        </div>

        {/* Scroll cue */}
        <div className="absolute bottom-10 inset-x-0 flex flex-col items-center text-[#1F1B17] z-40" style={{ opacity: Math.max(0, 1 - p * 3) }}>
          <div className="text-[10px] tracking-[0.5em] uppercase">Scroll</div>
          <ChevronDown className="animate-bounce mt-2" size={18} />
        </div>
      </div>
    </section>
  );
}
